/** Layout map size (Interactive4 ImageMap card). */
export const INTERACTIVE4_MAP_W = 400;
export const INTERACTIVE4_MAP_H = 420;

/** Must match learner `btnSize` / editor hotspot outer size (POPUP / AUDIO_POPUP / AudioPoint). */
export const INTERACTIVE4_BTN_SIZE = 64;

function hasPositiveSize(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Solid-color backgrounds are stored as 1×1 px images. Using such size as the
 * %-coordinate basis destroys hotspot math (percent basis = 1px, overlap
 * safe-distance = 6400%). Any dimension below this is treated as "no size".
 */
export const MIN_USABLE_HOTSPOT_IMAGE_PX = 16;

/**
 * Returns the image size if it is usable as a hotspot coordinate basis,
 * otherwise null (caller should fall back to its default, e.g. 1024).
 */
export function usableHotspotImageSize(
  width?: number | null,
  height?: number | null
): { width: number; height: number } | null {
  if (
    hasPositiveSize(width) &&
    width >= MIN_USABLE_HOTSPOT_IMAGE_PX &&
    hasPositiveSize(height) &&
    height >= MIN_USABLE_HOTSPOT_IMAGE_PX
  ) {
    return { width, height };
  }
  return null;
}

/**
 * Clamp hotspot pixel coords into the image rectangle.
 * If width/height are missing or invalid, returns `{ x, y }` unchanged
 * (do not invent a fallback size for clamping).
 */
export function clampHotspotPixelsToImage(
  x: number,
  y: number,
  imageWidth?: number | null,
  imageHeight?: number | null
): { x: number; y: number } {
  if (!hasPositiveSize(imageWidth) || !hasPositiveSize(imageHeight)) {
    return { x, y };
  }
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return {
      x: Number.isFinite(x) ? Math.max(0, Math.min(imageWidth, x)) : 0,
      y: Number.isFinite(y) ? Math.max(0, Math.min(imageHeight, y)) : 0
    };
  }
  return {
    x: Math.max(0, Math.min(imageWidth, x)),
    y: Math.max(0, Math.min(imageHeight, y))
  };
}

/**
 * Fullscreen / large intrinsic images use mapW ≫ phone width: (btn/mapW)*100 becomes tiny in
 * %-points while hotspots use the same %-space as the rendered box — on a narrow viewport a
 * few %-points are still far less than 64px. Floor safe distance using a conservative small
 * viewport so overlap resolution matches what learners actually see (see Interactive3 / ENG-9451).
 *
 * The image renders "contained" in the viewport with its aspect ratio preserved
 * (ImageMap2 fullscreen: width 100vw / height auto, or height 100vh / width auto).
 * So the floor must be taken from the image box as it would render on the minimal
 * viewport, per axis — not from the raw viewport sides. E.g. a landscape image on a
 * 360px-wide phone is only ~360*(h/w) px tall, and a %-point of `top` is worth far
 * fewer pixels than a %-point of `left`.
 */
const MIN_ASSUMED_VIEWPORT_W = 360;
const MIN_ASSUMED_VIEWPORT_H = 640;

const OVERLAP_GAP = 2;

type Point = { left: number; top: number };

type LayoutBounds = {
  bMinX: number;
  bMinY: number;
  bMaxX: number;
  bMaxY: number;
};

export function safeDistancesPercent(
  mapW: number,
  mapH: number,
  gap = OVERLAP_GAP
): { safeX: number; safeY: number } {
  const scale = Math.min(
    MIN_ASSUMED_VIEWPORT_W / mapW,
    MIN_ASSUMED_VIEWPORT_H / mapH
  );
  const renderedW = mapW * scale;
  const renderedH = mapH * scale;

  const safeX =
    Math.max(
      (INTERACTIVE4_BTN_SIZE / mapW) * 100,
      (INTERACTIVE4_BTN_SIZE / renderedW) * 100
    ) + gap;
  const safeY =
    Math.max(
      (INTERACTIVE4_BTN_SIZE / mapH) * 100,
      (INTERACTIVE4_BTN_SIZE / renderedH) * 100
    ) + gap;
  return { safeX, safeY };
}

function getLayoutBounds(
  isMobile: boolean,
  mapW: number,
  mapH: number
): LayoutBounds {
  const halfBtnX = (INTERACTIVE4_BTN_SIZE / 2 / mapW) * 100;
  const halfBtnY = (INTERACTIVE4_BTN_SIZE / 2 / mapH) * 100;
  return {
    bMinX: halfBtnX,
    bMinY: halfBtnY,
    bMaxX: isMobile ? 80 : 100 - halfBtnX,
    bMaxY: isMobile ? 80 : 100 - halfBtnY
  };
}

function clampPointToBounds(p: Point, bounds: LayoutBounds): void {
  p.left = Math.max(bounds.bMinX, Math.min(bounds.bMaxX, p.left));
  p.top = Math.max(bounds.bMinY, Math.min(bounds.bMaxY, p.top));
}

function pointsOverlap(
  a: Point,
  b: Point,
  safeX: number,
  safeY: number
): boolean {
  return Math.abs(a.left - b.left) < safeX && Math.abs(a.top - b.top) < safeY;
}

/** True when the candidate is separated from every other point on at least one axis. */
function clearOfAllPoints(
  nL: number,
  nT: number,
  points: ReadonlyArray<Point>,
  skipIdx: number,
  safeX: number,
  safeY: number
): boolean {
  for (let i = 0; i < points.length; i++) {
    if (i === skipIdx) continue;
    if (
      Math.abs(nL - points[i].left) < safeX &&
      Math.abs(nT - points[i].top) < safeY
    ) {
      return false;
    }
  }
  return true;
}

function clearOfFixedOthers(
  nL: number,
  nT: number,
  fixedOthers: ReadonlyArray<Point>,
  safeX: number,
  safeY: number
): boolean {
  for (let i = 0; i < fixedOthers.length; i++) {
    const fo = fixedOthers[i];
    if (Math.abs(nL - fo.left) < safeX && Math.abs(nT - fo.top) < safeY) {
      return false;
    }
  }
  return true;
}

/**
 * Find a position inside bounds that does not overlap any fixed points.
 * Expands in rings from the preferred anchor so layout stays close to the author intent.
 */
function findClearPosition(
  preferred: Point,
  fixedPoints: ReadonlyArray<Point>,
  bounds: LayoutBounds,
  safeX: number,
  safeY: number
): Point {
  const clampCandidate = (nL: number, nT: number): Point => ({
    left: Math.max(bounds.bMinX, Math.min(bounds.bMaxX, nL)),
    top: Math.max(bounds.bMinY, Math.min(bounds.bMaxY, nT))
  });

  const tryCandidate = (nL: number, nT: number): Point | null => {
    const c = clampCandidate(nL, nT);
    if (clearOfFixedOthers(c.left, c.top, fixedPoints, safeX, safeY)) {
      return c;
    }
    return null;
  };

  const anchor = clampCandidate(preferred.left, preferred.top);
  const direct = tryCandidate(anchor.left, anchor.top);
  if (direct) return direct;

  const offsets: Array<[number, number]> = [];
  const maxRing = 24;
  for (let ring = 1; ring <= maxRing; ring++) {
    for (let row = -ring; row <= ring; row++) {
      for (let col = -ring; col <= ring; col++) {
        if (Math.abs(row) !== ring && Math.abs(col) !== ring) continue;
        offsets.push([col * safeX, row * safeY]);
      }
    }
  }

  for (const [dL, dT] of offsets) {
    const placed = tryCandidate(anchor.left + dL, anchor.top + dT);
    if (placed) return placed;
  }

  const stepX = safeX / 2;
  const stepY = safeY / 2;
  for (let top = bounds.bMinY; top <= bounds.bMaxY; top += stepY) {
    for (let left = bounds.bMinX; left <= bounds.bMaxX; left += stepX) {
      const placed = tryCandidate(left, top);
      if (placed) return placed;
    }
  }

  return anchor;
}

/** Spread points that share the same %-anchor (e.g. after edge clamp) before overlap passes. */
function spreadCoincidentPoints(
  points: Point[],
  bounds: LayoutBounds,
  safeX: number,
  safeY: number
): void {
  const groups = new Map<string, number[]>();

  for (let i = 0; i < points.length; i++) {
    const key = `${points[i].left.toFixed(2)}:${points[i].top.toFixed(2)}`;
    const bucket = groups.get(key);
    if (bucket) bucket.push(i);
    else groups.set(key, [i]);
  }

  for (const indices of groups.values()) {
    if (indices.length < 2) continue;

    for (let j = 1; j < indices.length; j++) {
      const idx = indices[j];
      const fixed = points.filter((_, i) => i !== idx);
      const placed = findClearPosition(
        points[idx],
        fixed,
        bounds,
        safeX,
        safeY
      );
      points[idx].left = placed.left;
      points[idx].top = placed.top;
    }
  }
}

/** Last-resort pass: force any remaining overlaps apart using findClearPosition. */
function forceResolveRemainingOverlaps(
  points: Point[],
  bounds: LayoutBounds,
  safeX: number,
  safeY: number
): void {
  for (let pass = 0; pass < 240; pass++) {
    let moved = false;

    for (let i = 0; i < points.length; i++) {
      for (let j = i + 1; j < points.length; j++) {
        if (!pointsOverlap(points[i], points[j], safeX, safeY)) continue;

        const fixed = points.filter((_, k) => k !== j);
        const placed = findClearPosition(
          points[j],
          fixed,
          bounds,
          safeX,
          safeY
        );
        points[j].left = placed.left;
        points[j].top = placed.top;
        moved = true;

        if (!pointsOverlap(points[i], points[j], safeX, safeY)) break;
      }
    }

    if (!moved) break;
  }
}

/**
 * Same overlap resolution as learner template: nudge anchors in reading order until stable.
 * @param mapW - actual container/image width in pixels (defaults to INTERACTIVE4_MAP_W=400)
 * @param mapH - actual container/image height in pixels (defaults to INTERACTIVE4_MAP_H=420)
 * @see Interactive4 translator
 */
export function resolveInteractive4Overlaps(
  points: Array<{ left: number; top: number }>,
  isMobile: boolean,
  mapW = INTERACTIVE4_MAP_W,
  mapH = INTERACTIVE4_MAP_H
): void {
  if (points.length < 2) return;

  const { safeX, safeY } = safeDistancesPercent(mapW, mapH, OVERLAP_GAP);
  const bounds = getLayoutBounds(isMobile, mapW, mapH);

  spreadCoincidentPoints(points, bounds, safeX, safeY);

  const order = points
    .map((_, i) => i)
    .sort(
      (a, b) =>
        points[a].top + points[a].left - (points[b].top + points[b].left)
    );

  for (let pass = 0; pass < 120; pass++) {
    let anyMoved = false;

    for (let k = 1; k < order.length; k++) {
      const idx = order[k];

      let worstOther = -1;
      let worstArea = 0;

      for (let m = 0; m < order.length; m++) {
        if (m === k) continue;
        const o = order[m];
        const dx = Math.abs(points[idx].left - points[o].left);
        const dy = Math.abs(points[idx].top - points[o].top);
        if (dx >= safeX || dy >= safeY) continue;
        const area = (safeX - dx) * (safeY - dy);
        if (area > worstArea) {
          worstArea = area;
          worstOther = o;
        }
      }

      if (worstOther === -1) continue;

      const relX = points[idx].left - points[worstOther].left;
      const relY = points[idx].top - points[worstOther].top;
      const EPS = 0.15;
      const needX = safeX - Math.abs(relX) + EPS;
      const needY = safeY - Math.abs(relY) + EPS;
      const saved = { left: points[idx].left, top: points[idx].top };

      const tryNudge = (dL: number, dT: number): boolean => {
        const nL = saved.left + dL;
        const nT = saved.top + dT;
        if (
          nL < bounds.bMinX ||
          nL > bounds.bMaxX ||
          nT < bounds.bMinY ||
          nT > bounds.bMaxY
        ) {
          return false;
        }
        if (!clearOfAllPoints(nL, nT, points, idx, safeX, safeY)) {
          return false;
        }
        points[idx].left = nL;
        points[idx].top = nT;
        return true;
      };

      const dirX = relX >= 0 ? 1 : -1;
      const dirY = relY >= 0 ? 1 : -1;

      const moved =
        needX <= needY
          ? tryNudge(dirX * needX, 0) ||
            tryNudge(0, dirY * needY) ||
            tryNudge(-dirX * needX, 0) ||
            tryNudge(0, -dirY * needY) ||
            tryNudge(dirX * needX, dirY * needY)
          : tryNudge(0, dirY * needY) ||
            tryNudge(dirX * needX, 0) ||
            tryNudge(0, -dirY * needY) ||
            tryNudge(-dirX * needX, 0) ||
            tryNudge(dirX * needX, dirY * needY);

      if (!moved) {
        const fixed = points.filter((_, i) => i !== idx);
        const placed = findClearPosition(saved, fixed, bounds, safeX, safeY);
        points[idx].left = placed.left;
        points[idx].top = placed.top;
      }

      anyMoved = true;
    }

    if (!anyMoved) break;
  }

  stabilizeInteractive4Layout(points, bounds, safeX, safeY);
}

/** Clamp to bounds, then re-resolve — final clamp can shrink edge anchors back into overlap. */
function stabilizeInteractive4Layout(
  points: Point[],
  bounds: LayoutBounds,
  safeX: number,
  safeY: number
): void {
  for (let pass = 0; pass < 8; pass++) {
    forceResolveRemainingOverlaps(points, bounds, safeX, safeY);
    for (const p of points) clampPointToBounds(p, bounds);

    let stillOverlapping = false;
    for (let i = 0; i < points.length; i++) {
      for (let j = i + 1; j < points.length; j++) {
        if (pointsOverlap(points[i], points[j], safeX, safeY)) {
          stillOverlapping = true;
          break;
        }
      }
      if (stillOverlapping) break;
    }
    if (!stillOverlapping) break;
  }
}

/**
 * Editor drag: only the proposed point moves; fixed others stay. Same safe distance as resolveInteractive4Overlaps.
 * @param mapW - actual container/image width in pixels (defaults to INTERACTIVE4_MAP_W=400)
 * @param mapH - actual container/image height in pixels (defaults to INTERACTIVE4_MAP_H=420)
 */
export function nudgeDraggedHotspotAwayFromFixed(
  proposed: { left: number; top: number },
  fixedOthers: ReadonlyArray<{ left: number; top: number }>,
  isMobile: boolean,
  mapW = INTERACTIVE4_MAP_W,
  mapH = INTERACTIVE4_MAP_H
): { left: number; top: number } {
  const p = { ...proposed };
  if (fixedOthers.length === 0) return p;

  const { safeX, safeY } = safeDistancesPercent(mapW, mapH, OVERLAP_GAP);
  const bounds = getLayoutBounds(isMobile, mapW, mapH);

  for (let pass = 0; pass < 120; pass++) {
    let worstOther = -1;
    let worstArea = 0;

    for (let m = 0; m < fixedOthers.length; m++) {
      const o = fixedOthers[m];
      const dx = Math.abs(p.left - o.left);
      const dy = Math.abs(p.top - o.top);
      if (dx >= safeX || dy >= safeY) continue;
      const area = (safeX - dx) * (safeY - dy);
      if (area > worstArea) {
        worstArea = area;
        worstOther = m;
      }
    }

    if (worstOther === -1) break;

    const o = fixedOthers[worstOther];
    const relX = p.left - o.left;
    const relY = p.top - o.top;
    const EPS = 0.15;
    const needX = safeX - Math.abs(relX) + EPS;
    const needY = safeY - Math.abs(relY) + EPS;
    const saved = { left: p.left, top: p.top };

    const tryNudge = (dL: number, dT: number): boolean => {
      const nL = saved.left + dL;
      const nT = saved.top + dT;
      if (
        nL < bounds.bMinX ||
        nL > bounds.bMaxX ||
        nT < bounds.bMinY ||
        nT > bounds.bMaxY
      ) {
        return false;
      }
      if (!clearOfFixedOthers(nL, nT, fixedOthers, safeX, safeY)) {
        return false;
      }
      p.left = nL;
      p.top = nT;
      return true;
    };

    const dirX = relX >= 0 ? 1 : -1;
    const dirY = relY >= 0 ? 1 : -1;

    const moved =
      needX <= needY
        ? tryNudge(dirX * needX, 0) ||
          tryNudge(0, dirY * needY) ||
          tryNudge(-dirX * needX, 0) ||
          tryNudge(0, -dirY * needY) ||
          tryNudge(dirX * needX, dirY * needY)
        : tryNudge(0, dirY * needY) ||
          tryNudge(dirX * needX, 0) ||
          tryNudge(0, -dirY * needY) ||
          tryNudge(-dirX * needX, 0) ||
          tryNudge(dirX * needX, dirY * needY);

    if (!moved) {
      const placed = findClearPosition(
        saved,
        fixedOthers,
        bounds,
        safeX,
        safeY
      );
      p.left = placed.left;
      p.top = placed.top;
    }
  }

  clampPointToBounds(p, bounds);
  if (!clearOfFixedOthers(p.left, p.top, fixedOthers, safeX, safeY)) {
    const placed = findClearPosition(p, fixedOthers, bounds, safeX, safeY);
    p.left = placed.left;
    p.top = placed.top;
    clampPointToBounds(p, bounds);
  }
  return p;
}

/** Test helper: returns true if any pair of points still overlaps. */
export function hasInteractive4Overlaps(
  points: ReadonlyArray<Point>,
  mapW: number,
  mapH: number
): boolean {
  const { safeX, safeY } = safeDistancesPercent(mapW, mapH, OVERLAP_GAP);
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      if (pointsOverlap(points[i], points[j], safeX, safeY)) {
        return true;
      }
    }
  }
  return false;
}
